import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Modal, Badge, Alert, Dropdown } from 'react-bootstrap';
import api from "../../services/api";
import Loading from '../Shared/Loading';
import TableFull from '../Shared/CommonTable/TableFull';
import { toast } from 'react-toastify';
import { dateOnlyAsNumbers, timeOnlyFormatter } from '../../utils/dateFormatter';
import styles from './RecordAnalysisManagement.module.scss';

// Checkbox component that handles indeterminate state
function IndeterminateCheckbox({ indeterminate, ...rest }) {
  const ref = useRef(null);

  useEffect(() => {
    if (ref.current) {
      ref.current.indeterminate = indeterminate;
    }
  }, [indeterminate]);

  return <input type="checkbox" ref={ref} {...rest} />;
}

export default function RecordAnalysisManagement() {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [records, setRecords] = useState([]);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [selectedRecords, setSelectedRecords] = useState([]);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [actionType, setActionType] = useState(''); // 'clear-and-rerun' or 'clear-only'
  const [processing, setProcessing] = useState(false);
  const [resetSelection, setResetSelection] = useState(0);
  const [rowSelection, setRowSelection] = useState({});

  useEffect(() => {
    loadRecordsWithIssues();
  }, []);

  const loadRecordsWithIssues = async () => {
    setLoading(true);
    try {
      const response = await api.get('/settings/admin/records/analysis-issues');
      setRecords(response.data);
    } catch (error) {
      toast.error(t('admin.recordAnalysis.loadError'));
      console.error('Error loading records:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleClearAndRerun = (record) => {
    setSelectedRecord(record);
    setActionType('clear-and-rerun');
    setShowConfirmModal(true);
  };

  const handleClearOnly = (record) => {
    setSelectedRecord(record);
    setActionType('clear-only');
    setShowConfirmModal(true);
  };

  const handleCloseOnline = (record) => {
    setSelectedRecord(record);
    setActionType('close-online');
    setShowConfirmModal(true);
  };

  const handleBulkAction = (action) => {
    const selectedIds = Object.keys(rowSelection).filter(key => rowSelection[key]);
    const selectedItems = records.filter((_, index) => selectedIds.includes(String(index)));
    
    if (selectedItems.length === 0) {
      toast.warning(t('admin.recordAnalysis.noRecordsSelected'));
      return;
    }
    
    setSelectedRecords(selectedItems);
    setActionType(action);
    setShowConfirmModal(true);
  };

  const confirmAction = async () => {
    if (!selectedRecord && selectedRecords.length === 0) return;

    setProcessing(true);
    setShowConfirmModal(false);

    const recordsToProcess = selectedRecords.length > 0 ? selectedRecords : [selectedRecord];
    const isBulk = selectedRecords.length > 0;

    try {
      if (isBulk) {
        // Bulk operation
        const endpoint = actionType === 'clear-and-rerun' 
          ? '/settings/admin/records/bulk-clear-analysis'
          : '/settings/admin/records/bulk-clear-analysis-only';
        
        const recordIds = recordsToProcess.map(r => r.recordId);
        const response = await api.post(endpoint, { recordIds });
        
        toast.success(response.data.message || t('admin.recordAnalysis.bulkSuccess', { count: recordIds.length }));
      } else {
        // Single operation
        const endpointByAction = {
          'clear-and-rerun': `/settings/admin/record/${selectedRecord.recordId}/clear-analysis`,
          'clear-only': `/settings/admin/record/${selectedRecord.recordId}/clear-analysis-only`,
          'close-online': `/settings/admin/record/${selectedRecord.recordId}/close-online`,
        };

        const response = await api.post(endpointByAction[actionType]);
        
        toast.success(response.data.message || t('admin.recordAnalysis.success'));
      }
      
      // Reload the list
      await loadRecordsWithIssues();
      
      // Reset table selection
      setResetSelection(prev => prev + 1);
      setRowSelection({});
    } catch (error) {
      toast.error(t('admin.recordAnalysis.actionError'));
      console.error('Error performing action:', error);
    } finally {
      setProcessing(false);
      setSelectedRecord(null);
      setSelectedRecords([]);
      setActionType('');
    }
  };


  const formatDuration = (ms) => {
    if (!ms) return '-';
    const seconds = Math.floor(ms / 1000);
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);
    
    if (hours > 0) {
      return `${hours}h ${minutes % 60}m`;
    } else if (minutes > 0) {
      return `${minutes}m ${seconds % 60}s`;
    }
    return `${seconds}s`;
  };

  // OnlineRecordStates: 10 = WaitingForData, 20 = IsReceivingNow
  const isOnlineNotFinished = (state) => state === 10 || state === 20;

  const getProcessingStatusBadge = (status) => {
    const statusMap = {
      0: { variant: 'secondary', text: t('admin.recordAnalysis.status.notProcessed') },
      10: { variant: 'warning', text: t('admin.recordAnalysis.status.processing') },
      20: { variant: 'success', text: t('admin.recordAnalysis.status.done') },
      30: { variant: 'info', text: t('admin.recordAnalysis.status.onlineProcessing') }
    };

    const statusInfo = statusMap[status] || { variant: 'secondary', text: 'Unknown' };
    return <Badge bg={statusInfo.variant}>{statusInfo.text}</Badge>;
  };

  const columns = useMemo(() => [
    {
      id: 'select',
      header: ({ table }) => (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', padding: 0 }}>
          <IndeterminateCheckbox
            checked={table.getIsAllPageRowsSelected()}
            indeterminate={table.getIsSomePageRowsSelected()}
            onChange={table.getToggleAllPageRowsSelectedHandler()}
          />
        </div>
      ),
      cell: ({ row }) => (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', padding: 0 }}>
          <input
            type="checkbox"
            checked={row.getIsSelected()}
            onChange={row.getToggleSelectedHandler()}
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      ),
      size: 40,
      className: styles.checkBoxStyle
    },
    {
      header: t('admin.recordAnalysis.recordId'),
      accessorKey: 'recordId',
      cell: ({ row }) => {
        const recordId = row.original.recordId;
        const userUid = row.original.userUid;
        const recordUid = row.original.recordUid;
        
        if (userUid && recordUid) {
          const recordUrl = `/user/${userUid}/records/${recordUid}`;
          return (
            <a 
              href={recordUrl} 
              target="_blank" 
              rel="noopener noreferrer"
              style={{ textDecoration: 'none', color: '#007bff' }}
            >
              {recordId}
            </a>
          );
        }
        return recordId;
      },
    },
    {
      header: t('admin.recordAnalysis.user'),
      accessorKey: 'userFullName',
      cell: info => info.getValue() || '-',
    },
    {
      header: t('admin.recordAnalysis.recordDate'),
      accessorKey: 'dateRecordedUtc',
      cell: info => dateOnlyAsNumbers(info.getValue()),
    },
    {
      header: t('admin.recordAnalysis.recordTime'),
      id: 'recordTime',
      accessorKey: 'dateRecordedUtc',
      cell: info => timeOnlyFormatter(info.getValue()),
    },
    {
      header: t('admin.recordAnalysis.duration'),
      accessorKey: 'recordLengthMs',
      cell: info => formatDuration(info.getValue()),
    },
    {
      header: t('admin.recordAnalysis.statusHeader'),
      accessorKey: 'processingStatus',
      cell: ({ row }) => (
        <div className={styles.statusCell}>
          {getProcessingStatusBadge(row.original.processingStatus)}
          {isOnlineNotFinished(row.original.onlineRecordState) && (
            <Badge bg="danger">{t('admin.recordAnalysis.onlineNotFinished')}</Badge>
          )}
        </div>
      ),
    },
    {
      header: t('admin.recordAnalysis.processedParts'),
      accessorKey: 'processedPartsCount',
      cell: info => info.getValue() || 0,
    },
    {
      header: t('admin.recordAnalysis.lastModified'),
      accessorKey: 'lastModifiedUtc',
      cell: info => dateOnlyAsNumbers(info.getValue()),
    },
    {
      header: t('admin.recordAnalysis.actions'),
      id: 'actions',
      cell: ({ row }) => {
        const canCloseOnline = isOnlineNotFinished(row.original.onlineRecordState);
        return (
          <div className={styles.actionsCell} onClick={(e) => e.stopPropagation()}>
            <Dropdown align="end">
              <Dropdown.Toggle
                variant="outline-secondary"
                size="sm"
                bsPrefix="btn"
                disabled={processing}
                title={t('admin.recordAnalysis.actionsMenu')}
              >
                <i className="bi bi-three-dots-vertical"></i>
              </Dropdown.Toggle>
              <Dropdown.Menu>
                <Dropdown.Item
                  onClick={() => handleClearAndRerun(row.original)}
                  title={t('admin.recordAnalysis.clearAndRerunTooltip')}
                >
                  {t('admin.recordAnalysis.clearAndRerun')}
                </Dropdown.Item>
                <Dropdown.Item
                  onClick={() => handleClearOnly(row.original)}
                  title={t('admin.recordAnalysis.clearOnlyTooltip')}
                >
                  {t('admin.recordAnalysis.clearOnly')}
                </Dropdown.Item>
                {canCloseOnline && (
                  <Dropdown.Item
                    onClick={() => handleCloseOnline(row.original)}
                    title={t('admin.recordAnalysis.closeOnlineTooltip')}
                  >
                    {t('admin.recordAnalysis.closeOnline')}
                  </Dropdown.Item>
                )}
              </Dropdown.Menu>
            </Dropdown>
          </div>
        );
      },
    },
  ], [t, processing]);

  if (loading) {
    return <Loading />;
  }

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <h3>{t('admin.recordAnalysis.title')}</h3>
        <div className={styles.headerActions}>
          {Object.keys(rowSelection).filter(key => rowSelection[key]).length > 0 && (
            <div className={styles.bulkActions}>
              <span className={styles.selectedCount}>
                {t('admin.recordAnalysis.selectedCount', { count: Object.keys(rowSelection).filter(key => rowSelection[key]).length })}
              </span>
              <Button 
                variant="warning" 
                size="sm"
                onClick={() => handleBulkAction('clear-and-rerun')}
                disabled={processing}
              >
                {t('admin.recordAnalysis.bulkClearAndRerun')}
              </Button>
              <Button 
                variant="danger" 
                size="sm"
                onClick={() => handleBulkAction('clear-only')}
                disabled={processing}
              >
                {t('admin.recordAnalysis.bulkClearOnly')}
              </Button>
            </div>
          )}
          <Button variant="outline-primary" onClick={loadRecordsWithIssues}>
            {t('admin.recordAnalysis.refresh')}
          </Button>
        </div>
      </div>

      {records.length === 0 ? (
        <div className={styles.emptyState}>
          <Alert variant="info">
            {t('admin.recordAnalysis.noIssues')}
          </Alert>
        </div>
      ) : (
        <div className={styles.tableWrapper}>
          <TableFull
          data={records}
          columns={columns}
          styles={styles}
          resetSelection={resetSelection}
          allowColumnHeaderSort={true}
          allowGlobalFilter={true}
          rowSelection={rowSelection}
          setRowSelection={setRowSelection}
          initialStateSettings={{
            pagination: {
              pageSize: 20,
            },
            sorting: [
              {
                id: 'lastModifiedUtc',
                desc: true,
              },
            ],
          }}
          mainTableClassName={styles.table}
          />
        </div>
      )}

      <Modal show={showConfirmModal} onHide={() => setShowConfirmModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>
            {actionType === 'clear-and-rerun'
              ? t('admin.recordAnalysis.confirmClearAndRerun')
              : actionType === 'close-online'
                ? t('admin.recordAnalysis.confirmCloseOnline')
                : t('admin.recordAnalysis.confirmClearOnly')}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className={styles.modalContent}>
          {(selectedRecord || selectedRecords.length > 0) && (
            <div className={styles.recordInfo}>
              {selectedRecords.length > 0 ? (
                <>
                  <p>
                    <strong>{t('admin.recordAnalysis.selectedRecords')}:</strong> {selectedRecords.length}
                  </p>
                  <div className={styles.selectedRecordsList}>
                    {selectedRecords.slice(0, 5).map(record => (
                      <div key={record.recordId}>
                        {t('admin.recordAnalysis.recordId')}: {record.recordId} - {record.userFullName}
                      </div>
                    ))}
                    {selectedRecords.length > 5 && (
                      <div>...{t('admin.recordAnalysis.andMore', { count: selectedRecords.length - 5 })}</div>
                    )}
                  </div>
                </>
              ) : (
                <>
                  <p>
                    <strong>{t('admin.recordAnalysis.recordId')}:</strong> {selectedRecord.recordId}
                  </p>
                  <p>
                    <strong>{t('admin.recordAnalysis.user')}:</strong> {selectedRecord.userFullName}
                  </p>
                </>
              )}
              <Alert variant="warning">
                {selectedRecords.length > 0
                  ? (actionType === 'clear-and-rerun'
                    ? t('admin.recordAnalysis.warningBulkClearAndRerun', { count: selectedRecords.length })
                    : t('admin.recordAnalysis.warningBulkClearOnly', { count: selectedRecords.length }))
                  : (actionType === 'clear-and-rerun'
                    ? t('admin.recordAnalysis.warningClearAndRerun')
                    : actionType === 'close-online'
                      ? t('admin.recordAnalysis.warningCloseOnline')
                      : t('admin.recordAnalysis.warningClearOnly'))}
              </Alert>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowConfirmModal(false)}>
            {t('common.cancel')}
          </Button>
          <Button 
            variant={actionType === 'clear-and-rerun' ? 'warning' : 'danger'}
            onClick={confirmAction}
          >
            {t('common.confirm')}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}